import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Chip, Divider, Stack, Avatar, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { sendPasswordResetEmail, signOut } from 'firebase/auth';
import theme from '../theme';
import Navbar from './Navbar';
import Footer from './Footer';
import { auth } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { openPortal } from '../lib/billingApi';
import { BOOK_URL, DONATION_URL } from '../config';
import { useLang, useT } from '../i18n/LanguageContext';

function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function getSourceLabel(t) {
  return {
    subscription: t('profile.sourceSubscription'),
    donation: t('profile.sourceDonation'),
    code: t('profile.sourceCode'),
    admin: t('profile.sourceAdmin'),
  };
}

function getSubStatusLabel(t) {
  return {
    active: t('profile.statusActive'),
    trialing: t('profile.statusTrialing'),
    past_due: t('profile.statusPastDue'),
    canceled: t('profile.statusCanceled'),
  };
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { currentUser, role, isPro, entitlement, subscription, refreshUserData } = useAuth();
  const { dateLocale } = useLang();
  const t = useT();
  const SOURCE_LABEL = getSourceLabel(t);
  const SUB_STATUS_LABEL = getSubStatusLabel(t);

  const [name, setName] = useState(currentUser?.displayName || '');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const tier = role === 'Admin' ? 'Admin' : (isPro ? 'Pro' : 'Free');
  const tierColor = { Admin: '#d32f2f', Pro: theme.home.cyanText, Free: '#6b7280' }[tier];
  const initial = (currentUser?.email || '?').charAt(0).toUpperCase();
  const isSub = entitlement?.source === 'subscription';
  // Subscriptions renew (no expiry on the entitlement) — use the stored
  // currentPeriodEnd; donations/codes have a hard expiry date.
  const renewal = isSub ? toDate(subscription?.currentPeriodEnd) : toDate(entitlement?.expiresAt);
  const fmtDate = (d) => (d ? d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' }) : null);

  const notify = (severity, msg) => setToast({ severity, msg });

  const saveName = async () => {
    setBusy(true);
    try {
      await updateDoc(doc(getFirestore(), 'users', currentUser.uid), { displayName: name.trim() });
      await refreshUserData();
      notify('success', t('profile.updateSuccess'));
    } catch (e) {
      notify('error', e.message || t('profile.saveErrorFallback'));
    }
    setBusy(false);
  };

  const resetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      notify('success', t('profile.resetEmailSent', { email: currentUser.email }));
    } catch (e) {
      notify('error', e.message || t('profile.resetErrorFallback'));
    }
  };

  const go = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      const key = e.code ? `errors.${e.code}` : null;
      const msg = key && t(key) !== key ? t(key) : e.message;
      notify('error', msg);
      setBusy(false);
    }
  };

  const card = {
    bgcolor: '#fff', borderRadius: 4, p: { xs: 3, md: 4 },
    border: `1px solid ${theme.home.paperDeep}`, boxShadow: theme.home.softShadow,
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: theme.home.paper, minHeight: '100vh' }}>
        <Navbar />
        <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2.5, md: 3 }, py: { xs: 5, md: 7 } }}>
          <Typography variant="h3" sx={{ fontFamily: theme.home.display, fontWeight: 700, color: theme.home.ink, mb: 4 }}>
            {t('profile.heading')}
          </Typography>

          {/* Identity */}
          <Box sx={{ ...card, display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
            <Avatar sx={{ bgcolor: tierColor, width: 64, height: 64, fontSize: 28, fontFamily: theme.home.display }}>{initial}</Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{currentUser?.displayName || currentUser?.email}</Typography>
              <Typography sx={{ color: theme.home.inkMuted, fontSize: '0.95rem' }}>{currentUser?.email}</Typography>
            </Box>
            <Chip label={tier} sx={{ bgcolor: tierColor, color: '#fff', fontWeight: 700 }} />
          </Box>

          {/* Account */}
          <Box sx={{ ...card, mb: 3 }}>
            <Typography variant="h6" sx={{ fontFamily: theme.home.display, mb: 2 }}>{t('profile.accountHeading')}</Typography>
            <TextField
              label={t('profile.displayNameLabel')} value={name} onChange={(e) => setName(e.target.value)}
              fullWidth size="small" sx={{ mb: 2 }}
            />
            <TextField label={t('profile.emailLabel')} value={currentUser?.email || ''} fullWidth size="small" disabled sx={{ mb: 2.5 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" onClick={saveName} disabled={busy}
                sx={{ borderRadius: 999, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover } }}>
                {t('profile.saveButton')}
              </Button>
              <Button variant="text" onClick={resetPassword} sx={{ borderRadius: 999, color: theme.home.ink }}>
                {t('profile.resetPasswordButton')}
              </Button>
            </Stack>
          </Box>

          {/* Subscription / access */}
          <Box sx={{ ...card, mb: 3 }}>
            <Typography variant="h6" sx={{ fontFamily: theme.home.display, mb: 2 }}>{t('profile.accessHeading')}</Typography>
            <Stack spacing={1.2} sx={{ mb: 2.5 }}>
              <Row k={t('profile.planLabel')} v={<Chip size="small" label={tier} sx={{ bgcolor: tierColor, color: '#fff', fontWeight: 700 }} />} />
              {entitlement?.source && <Row k={t('profile.sourceLabel')} v={SOURCE_LABEL[entitlement.source] || entitlement.source} />}
              {isSub && subscription?.status && (
                <Row k={t('profile.statusLabel')} v={SUB_STATUS_LABEL[subscription.status] || subscription.status} />
              )}
              {isPro && role !== 'Admin' && (
                <Row k={isSub ? t('profile.nextRenewalLabel') : t('profile.validUntilLabel')}
                  v={fmtDate(renewal) || (isSub ? t('profile.renewalPending') : t('profile.noExpiry'))} />
              )}
            </Stack>
            <Divider sx={{ mb: 2.5 }} />
            {role === 'Admin' ? (
              <Typography sx={{ color: theme.home.inkMuted }}>{t('profile.adminFullAccess')}</Typography>
            ) : isPro ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {entitlement?.source === 'subscription' && (
                  <Button variant="outlined" onClick={() => go(openPortal)} disabled={busy}
                    sx={{ borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink }}>
                    {t('profile.manageSubscriptionButton')}
                  </Button>
                )}
                <Button variant="text" onClick={() => navigate('/redeem')} sx={{ borderRadius: 999, color: theme.home.coral }}>
                  {t('profile.requestAccessWithBookLink')}
                </Button>
              </Stack>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" component="a" href={BOOK_URL} target="_blank" rel="noopener noreferrer"
                  sx={{ borderRadius: 999, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover } }}>
                  {t('profile.unlockWithBookButton')}
                </Button>
                <Button variant="text" onClick={() => navigate('/redeem')} sx={{ borderRadius: 999, color: theme.home.coral }}>
                  {t('profile.haveBookRequestAccessLink')}
                </Button>
              </Stack>
            )}
          </Box>

          {/* Danger / session */}
          <Box sx={{ textAlign: 'center' }}>
            <Button component="a" href={DONATION_URL} target="_blank" rel="noopener noreferrer"
              sx={{ color: theme.home.coral, mb: 1 }}>
              {t('profile.supportProjectButton')}
            </Button>
            <br />
            <Button onClick={() => { signOut(auth); navigate('/'); }} sx={{ color: theme.home.inkMuted }}>
              {t('profile.signOutButton')}
            </Button>
          </Box>
        </Box>
        <Footer />

        <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
          <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

function Row({ k, v }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography sx={{ color: theme.home.inkMuted }}>{k}</Typography>
      <Box sx={{ fontWeight: 600 }}>{v}</Box>
    </Box>
  );
}
